import React, { useEffect, useState } from 'react'

import ErrorComponent from '../ErrorComponent'
import { SkeletonLoader } from '../../../commons/skeletonLoader'
import { reportError } from '../../sentry'
import GkdAuthParams from '../../../types/GkdAuthParams'
import {
  defaultLoaderDelayMs,
  hasOpener,
  isInIframe,
  isReturnVisit,
  sdkGraceMs,
  startProgrammaticPrequalification,
} from '../../../utils/prequalification/programmaticPrequalification'

const closeMessage = 'CLOSE'
const closeWindowTitle = 'You can close this window'
const closeWindowText = 'To see what you can spend, go back to the store and start again.'

export type ProgrammaticPrequalLoaderParams = {
  /** Loader locale, e.g. en_US */
  locale: string
  /** CMA base URL for the locale's region */
  adServerUrl: string
  purchaseAmount?: number
  currency?: string
  delay?: number
  clientId?: string
  partnerAccountId?: string
}

interface ProgrammaticPrequalLoaderProps {
  params: ProgrammaticPrequalLoaderParams
  gkdAuthParams: GkdAuthParams
  sendMessage: (data: object, message: string) => void
}

type LoaderState = 'loading' | 'failed' | 'returned'

const asString = (value: unknown): string => (typeof value === 'string' ? value : '')

/**
 * Loader for windows opened by Klarna.Messaging.Prequalification.initiate(). The SDK normally
 * replaces this page with the flow. A blocked popup the user releases later from the browser's
 * prompt has no SDK behind it, so it starts the flow itself instead of loading forever.
 */
export default function ProgrammaticPrequalLoader({
  params,
  gkdAuthParams,
  sendMessage,
}: ProgrammaticPrequalLoaderProps) {
  const [inIframe] = useState(isInIframe)
  const [state, setState] = useState<LoaderState>('loading')

  useEffect(() => {
    const fail = (error: Error) => {
      // Show the error before reporting, so a reporting failure can't leave the page loading forever.
      setState('failed')
      reportError({ inIframe, locale: params.locale }, error)
    }

    // The SDK controls its iframe: it swaps the URL or removes the iframe. Only offer a way out if it hangs.
    if (inIframe) {
      const delay = Number(params.delay) || defaultLoaderDelayMs
      const stuckTimeoutId = setTimeout(() => fail(new Error(`Prequalification loader was not replaced within ${delay}ms`)), delay)
      return () => clearTimeout(stuckTimeoutId)
    }

    const start = () => startProgrammaticPrequalification({
      baseUrl: params.adServerUrl,
      locale: params.locale,
      purchaseAmount: params.purchaseAmount,
      currency: params.currency,
      clientId: asString(gkdAuthParams.clientId) || params.clientId,
      integratorOrigin: asString(gkdAuthParams.integratorOrigin),
      accountId: asString(gkdAuthParams.accountId) || params.partnerAccountId,
    }).catch(fail)

    // No opener: no SDK can navigate this window.
    if (!hasOpener()) {
      start()
      return undefined
    }

    // Back, Forward or reload in the SDK's popup: the SDK won't replace the page again, and a new
    // flow here couldn't report its result to the merchant.
    if (isReturnVisit()) {
      setState('returned')
      return undefined
    }

    // Give the SDK time to replace the page; if it doesn't, nothing controls this window.
    const timeoutId = setTimeout(start, sdkGraceMs)
    const onPageHide = () => clearTimeout(timeoutId)
    // Restored from the back/forward cache after the SDK replaced it.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setState('returned')
      }
    }
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [])

  useEffect(() => {
    if (state !== 'loading') {
      // Hide the inline skeleton from learn-more/index.html so it can't show under the message.
      (window as Window & { __hideInitialSkeleton?: () => void }).__hideInitialSkeleton?.()
    }
  }, [state])

  if (state === 'loading') {
    return <SkeletonLoader />
  }

  // Inside the SDK iframe, CLOSE makes the SDK remove the iframe and resolve initiate().
  const onClose = inIframe
    ? () => sendMessage({}, closeMessage)
    : () => window.close()

  if (state === 'returned') {
    return <ErrorComponent title={closeWindowTitle} text={closeWindowText} onClose={onClose} />
  }

  return <ErrorComponent onClose={onClose} />
}
